import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ChevronDown } from 'lucide-react';
import { userService } from '../../_services';

function BonusProgram({
    titleText,
    whName,
    whId,
    whPrefix,
    whProfilePagin,
    whSearch,
    closeDatePopup,
    openEditProfilePopup
}) {
    const [bonusLoader, setBonusLoader] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [mhError, setMhError] = useState('');
    const [whError, setWhError] = useState('');
    const [userError, setUserError] = useState('');
    const [binData, setBinData] = useState([]);
    const [settingData, setSettingData] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [selectedBin, setSelectedBin] = useState('');
    const [whBonus, setWhBonus] = useState('');
    const [userBonus, setUserBonus] = useState('');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const monthOptions = [
        'February,2022', 'March,2022', 'April,2022', 'May,2022', 'June,2022',
        'July,2022', 'August,2022', 'September,2022', 'October,2022', 
        'November,2022', 'December,2022', 'January,2023'
    ];

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const getBinTier = useCallback(async (wholesalerId) => {
        try {
            const apidata = await userService.Bind_Tier_List(wholesalerId);
            if (apidata.length > 0 && apidata[0].binDetail?.length > 0) {
                setBinData(apidata[0].binDetail);
                setSelectedBin(apidata[0].binDetail[0]?.bin || '');
            }
        } catch (error) {

        }
    }, []);

    const fetchBonusSetting = useCallback(async () => {
        try {
            const apidata = await userService.Wh_Bonus_Setting('_', '_', whId);
            if (apidata.length > 0) {
                setSettingData(apidata);
            }
        } catch (error) {

        }
    }, [whId]);

    useEffect(() => {
        if (whId) {
            getBinTier(parseInt(whId));
            fetchBonusSetting();
        }
    }, [whId, getBinTier, fetchBonusSetting]);

    const handleMonthToggle = useCallback((month) => {
        setSelectedMonths(prev => {
            if (prev.includes(month)) {
                return prev.filter(m => m !== month);
            } else {
                return [...prev, month];
            }
        });
        setMhError('');
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setBonusLoader(true);
        setError('');
        setSuccessMessage('');
        setMhError('');
        setWhError('');
        setUserError('');

        let isValid = true;

        if (selectedMonths.length === 0) {
            setMhError('Please select at least one month');
            isValid = false;
        }

        if (!whBonus) {
            setWhError('Wholesaler bonus commission is required');
            isValid = false;
        } else if (!userService.validateCurrency(whBonus)) {
            setWhError('Invalid wholesaler bonus commission');
            isValid = false;
        } else if (parseFloat(whBonus) > 10) {
            setWhError('Wholesaler bonus commission should not be more than 10');
            isValid = false;
        }

        if (!userBonus) {
            setUserError('User bonus commission is required');
            isValid = false;
        } else if (!userService.validateCurrency(userBonus)) {
            setUserError('Invalid user bonus commission');
            isValid = false;
        } else if (parseFloat(userBonus) > 10) {
            setUserError('User bonus commission should not be more than 10');
            isValid = false;
        }

        if (!isValid) {
            setBonusLoader(false);
            return;
        }

        try {
            const monthName = selectedMonths.join('-');
            const apidata = await userService.BonusProgram(
                whId,
                monthName,
                whBonus,
                userBonus,
                selectedBin
            );

            if (apidata?.length > 0) {
                if (apidata[0].code === "1") {
                    setSuccessMessage(apidata[0].message);
                    setTimeout(() => {
                        fetchBonusSetting();
                        setSelectedMonths([]);
                        setWhBonus('');
                        setUserBonus('');
                    }, 2000);
                } else {
                    setError(apidata[0].message || 'Error!, Please try again.');
                }
            } else {
                setError('Error!, Please try again.');
            }
        } catch (error) {

            setError('Error!, Please try again.');
        } finally {
            setBonusLoader(false);
        }
    }, [selectedMonths, whBonus, userBonus, selectedBin, whId, fetchBonusSetting]);

    const handleCancel = useCallback(() => {
        closeDatePopup(whName, whId, whPrefix, whProfilePagin, whSearch);
        openEditProfilePopup(whName, whId, whPrefix, whProfilePagin, whSearch);
    }, [closeDatePopup, openEditProfilePopup, whName, whId, whPrefix, whProfilePagin, whSearch]);

    // Group bonus settings by level for display
    const groupedSettings = useMemo(() => {
        const distinctLevels = settingData.filter((obj, pos, arr) => {
            return arr.map(mapObj => mapObj["levelid"]).indexOf(obj["levelid"]) === pos;
        });

        return distinctLevels.map(level => {
            return settingData.filter(item => item.levelid === level.levelid);
        });
    }, [settingData]);

    return (
        <Dialog open={true} onOpenChange={handleCancel}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle>Bonus Program: {whName}</DialogTitle>
                </DialogHeader>

                <form id="bonus-program-form" onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Select Month[Year]</Label>
                            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button 
                                        type="button"
                                        variant="outline"
                                        className="flex h-9 w-full items-center justify-between whitespace-nowrap"
                                    >
                                        {selectedMonths.length > 0 ? (
                                            <span className="truncate">{selectedMonths.join(', ')}</span>
                                        ) : (
                                            "Select Month[s]"
                                        )}
                                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                    <div className="grid gap-1 p-2 max-h-[300px] overflow-auto">
                                        {monthOptions.map((month) => (
                                            <Label
                                                key={month}
                                                className="flex items-center space-x-2 cursor-pointer py-1 px-2 hover:bg-accent hover:text-accent-foreground rounded-sm"
                                            >
                                                <Checkbox
                                                    checked={selectedMonths.includes(month)}
                                                    onCheckedChange={() => handleMonthToggle(month)}
                                                />
                                                <span>{month}</span>
                                            </Label>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                            {mhError && <p className="text-sm text-destructive mt-1">{mhError}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label>BIN</Label>
                            <Select value={selectedBin} onValueChange={setSelectedBin} disabled={bonusLoader}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select BIN" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px] overflow-y-auto">
                                    {binData.map((item, index) => (
                                        <SelectItem key={index} value={item.bin}>
                                            {item.bin}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Wholesaler Bonus Commission</Label>
                            <Input
                                type="text"
                                value={whBonus}
                                onChange={(e) => {
                                    setWhBonus(e.target.value);
                                    setWhError('');
                                }}
                                disabled={bonusLoader}
                            />
                            {whError && <p className="text-sm text-destructive mt-1">{whError}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label>User Bonus Commission</Label>
                            <Input
                                type="text"
                                value={userBonus}
                                onChange={(e) => {
                                    setUserBonus(e.target.value);
                                    setUserError('');
                                }}
                                disabled={bonusLoader}
                            />
                            {userError && <p className="text-sm text-destructive mt-1">{userError}</p>}
                        </div>
                    </div>

                    {settingData.length > 0 && (
                        <div className="border rounded-lg p-4 space-y-3">
                            <h2 className="font-semibold text-lg">Running Bonus Program</h2>
                            
                            <div className="grid grid-cols-4 gap-2 font-medium text-sm border-b pb-2">
                                <div>Month(s)</div>
                                <div>Wh. Comm.</div>
                                <div>User Comm.</div>
                                <div>BIN</div>
                            </div>

                            <div className="space-y-2">
                                {groupedSettings.map((levelWiseList, index) => {
                                    if (levelWiseList.length > 0) {
                                        return (
                                            <div key={index} className="grid grid-cols-4 gap-2 text-sm py-2 border-b">
                                                <div className="flex flex-wrap gap-1">
                                                    {levelWiseList.map((item, idx) => (
                                                        <span key={idx} className="bg-muted px-2 py-1 rounded text-xs">
                                                            {item.monthname}
                                                        </span>
                                                    ))}
                                                </div>
                                                <div>{levelWiseList[0].whcomm}</div>
                                                <div>{levelWiseList[0].usercomm}</div>
                                                <div>{levelWiseList[0].bin}</div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                    )}

                    {successMessage && (
                        <div className="text-sm text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 p-3 rounded-md">
                            {successMessage}
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                            {error}
                        </div>
                    )}

                </form>
                <DialogFooter>
                    <Button type="submit" form="bonus-program-form" disabled={bonusLoader}>
                        {bonusLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save & Submit
                    </Button>
                    <Button type="button" variant="secondary" onClick={handleCancel} disabled={bonusLoader}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BonusProgram;
